import React, { useState, useRef, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import authService from '../services/auth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import config from "../config";
import BackButton from './BackButton';
import PoiCardLite from './poicardlite';
// import markerIcon from '../assets/images/nody_pin.svg'; // RIMOSSO - ora generato dinamicamente
import 'leaflet/dist/leaflet.css';
import { calculateDistance } from './utils';
import { calculateRouteDistance } from './routeUtils';
import useTranslation from './useTranslation';
import LocalIcon from './LocalIcon';
import { getOptimizedPoiImage } from '../utils/imageUtils';

const NEARBY_FILTER = "all";
const ITALY_CENTER = [41.9028, 12.4964];
const DEFAULT_ZOOM = 6;
const PADDING = 24;
const VERTICAL_OFFSET = -120;
const ANIMATION_DELAY = 100;

const Icon = ({ name, className, style }) => (
  <LocalIcon icon={name} className={className} style={style} />
);

const RouteInfoPanel = React.memo(({ route, pois, segmentDistances, onClose }) => {
  const { t } = useTranslation();
  
  if (!route || !pois || !Array.isArray(pois) || pois.length === 0) {
    return (
      <div className="position-fixed top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center" 
           style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
        <div className="bg-white rounded-4 w-100 position-relative overflow-y-auto"
             style={{ maxWidth: '95dvw', height: '70dvh' }}>
          <button
            onClick={onClose}
            className="btn bg-white close-button position-fixed z-1 opacity-75 border rounded-circle justify-content-center shadow-sm ratio-1x1 p-1 m-1 btn bg-white border rounded-circle d-flex align-items-center justify-content-center"
            style={{ top: '15dvh', right: 'calc(2.5dvw + 8px)' }}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>
          <div className="p-4">
            <p>{t('infoNonDisponibili')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="position-fixed top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center" 
         style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
      <div className="bg-white rounded-4 w-100 position-relative overflow-y-auto"
           style={{ maxWidth: '95dvw', height: '70dvh' }}>
        <button
          onClick={onClose}
          className="btn bg-white close-button position-fixed z-1 opacity-75 border rounded-circle justify-content-center shadow-sm ratio-1x1 p-1 m-1 btn bg-white border rounded-circle d-flex align-items-center justify-content-center"
          style={{ top: '15dvh', right: 'calc(2.5dvw + 8px)' }}
          aria-label="Close"
        >
          <Icon name="close" />
        </button>
        
        {route.field_immagine_percorso?.[0] && (
          <img
            src={route.field_immagine_percorso[0].url}
            alt={route.field_immagine_percorso[0].alt || ''}
            className="w-100"
            style={{ height: '200px', objectFit: 'cover' }}
          />
        )}

        <div className="p-4">
          <h3 className="fw-bold mb-3">{route.title?.[0]?.value || t('percorso')}</h3>
          <div className="d-flex gap-3 mb-3 flex-wrap">
            {route.field_tempi_medi_di_percorrenza?.[0]?.processed && (
              <span className="text-muted small d-flex align-items-center">
                <Icon name="schedule" className="me-1" style={{ fontSize: '0.875rem' }} />
                <span dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(route.field_tempi_medi_di_percorrenza[0].processed, 
                    { ALLOWED_TAGS: [] })
                }} />
              </span>
            )}
            <span className="text-muted small d-flex align-items-center">
              <Icon name="signpost" className="me-1" style={{ fontSize: '0.875rem' }} />
              {pois.length || 0} {t('tappe')}
            </span>
            <span className="text-muted small d-flex align-items-center">
              <Icon name="near_me" className="me-1" style={{ fontSize: '0.875rem' }} />
              {route.field_distanza?.[0]?.value || 
                (Array.isArray(segmentDistances) && segmentDistances.length > 0 
                  ? `${segmentDistances.reduce((a, b) => a + parseFloat(b.replace(' km', '')), 0).toFixed(1)} km`
                  : '...')}
            </span>
          </div>

          {route.field_descrizione_breve?.[0]?.value && (
            <div className="info-box mb-3 d-flex border align-items-center">
              <Icon name="info" style={{ width: '32px', marginRight: '10px' }} />
              <div 
                className="info-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(route.field_descrizione_breve[0].value)
                }}
              />
            </div>
          )}

          {route.body?.[0]?.value && (
            <div
              className="poi-body mb-4"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(route.body[0].value)
              }}
            />
          )}

          <h4 className="mb-3">{t('itinerario')}</h4>
          <div className="timeline position-relative">
            {Array.isArray(pois) && pois.map((poi, index) => (
              <React.Fragment key={poi?.nid?.[0]?.value || `poi-${index}`}>
                <div className="timeline-item-number position-relative mb-3">
                  <div className="position-absolute" style={{ 
                    width: '1px', 
                    background: 'rgb(204, 204, 204)', 
                    left: '33px',
                    height: index === pois.length - 1 ? '0' : 'calc(100% + 16px)',
                    zIndex: 0
                  }} />
                  
                  <div className="d-flex align-items-center" style={{ marginLeft: '55px' }}>
                    <div 
                      className="position-absolute d-flex align-items-center justify-content-center rounded-circle border-3 border border-black bg-white" 
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        fontSize: '16px', 
                        fontWeight: 'bold',
                        left: '16px',
                        zIndex: 1
                      }}
                    >
                      {index + 1}
                    </div>
                    <div className="fw-bold">{poi?.title?.[0]?.value || t('tappaSenzaNome')}</div>
                  </div>
                </div>

                {index < pois.length - 1 && (
                  <div className="timeline-item-direction position-relative mb-3">
                    <div className="position-absolute" style={{ 
                      width: '1px', 
                      background: 'rgb(204, 204, 204)', 
                      left: '33px',
                      height: 'calc(100% + 16px)',
                      zIndex: 0
                    }} />
                    
                    <div className="d-flex align-items-center" style={{ marginLeft: '55px' }}>
                      <div 
                        className="position-absolute d-flex align-items-center justify-content-center bg-white" 
                        style={{ 
                          width: '32px', 
                          height: 'auto', 
                          left: '16px',
                          zIndex: 1,
                        }}
                      >
                        <Icon name="directions" />
                      </div>
                      <span 
                        className="text-muted" 
                        style={{ 
                          fontSize: '0.875rem'
                        }}
                      >
                        {t('distanzaTappe')} {Array.isArray(segmentDistances) && segmentDistances[index] 
                          ? `${segmentDistances[index]}` 
                          : '...'}
                      </span>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

const calculateMarkerSize = (zoom) => {
  const maxSize = { width: 50, height: 60 };
  
  if (zoom >= 15) return {
    size: maxSize,
    anchor: {
      x: maxSize.width / 2,
      y: maxSize.height
    }
  };

  const zoomDiff = 15 - zoom;
  const reductionFactor = Math.max(0.2, 1 - (zoomDiff * 0.04));
  
  const size = {
    width: Math.round(maxSize.width * reductionFactor),
    height: Math.round(maxSize.height * reductionFactor)
  };

  return {
    size,
    anchor: {
      x: Math.round(size.width / 2),
      y: size.height
    }
  };
};

const RouteMap = () => {
  
  const { id } = useParams();
  const [pois, setPois] = useState([]);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [visiblePois, setVisiblePois] = useState([]);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialPoiDetailLoad, setInitialPoiDetailLoad] = useState(true);
  const [cardState, setCardState] = useState('hidden');
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUserMarkerSelected, setIsUserMarkerSelected] = useState(false);
  // Stati aggiuntivi per route
  const [route, setRoute] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);

  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  const { t } = useTranslation();

  const loadedTaxonomyIds = useRef(new Set());
  const mapRef = useRef(null);
  const sliderRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const routeCalculationDone = useRef(false);
  const taxonomyLoadInProgress = useRef(false);

  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) return null;

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (isFinite(latitude) && isFinite(longitude)) {
          setUserLocation({ latitude, longitude });
        }
      },
      (error) => console.error('Errore geolocalizzazione:', error),
      options
    );

    return watchId;
  }, []);

  const [segmentDistances, setSegmentDistances] = useState([]);
  
  const fetchOSRMRoute = useCallback(async (coordinates = null) => {
    try {
      if (routeCalculationDone.current) return;
      
      let result;
      if (coordinates && coordinates.length > 1) {
        const simplifiedPois = coordinates.map((coord) => ({
          field_geolocalizzazione: [{ lat: coord[0], lng: coord[1] }]
        }));
        
        result = await calculateRouteDistance(simplifiedPois);
      } else if (pois.length > 1) {
        result = await calculateRouteDistance(pois);
      } else {
        return;
      }
      
      if (result) {
        setRoutePath(result.coordinates);
        setSegmentDistances(result.distances || []);
        
        setRoute(prevRoute => {
          if (!prevRoute?.field_distanza?.[0]?.value) {
            return {
              ...prevRoute,
              field_distanza: [{ value: result.totalDistance }]
            };
          }
          return prevRoute;
        });
        
        routeCalculationDone.current = true;
      }
    } catch (error) {
      console.error('Errore nel routing:', error);
      routeCalculationDone.current = true;
    }
  }, [pois]);
  
  const loadTaxonomies = useCallback(async (pois) => {
    try {
      if (taxonomyLoadInProgress.current) return;
      taxonomyLoadInProgress.current = true;
      
      const taxonomyRequests = [];
      const uniqueTaxonomyIds = new Set();
      
      pois.forEach(poi => {
        const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
        if (taxonomyId && !loadedTaxonomyIds.current.has(taxonomyId) && !uniqueTaxonomyIds.has(taxonomyId)) {
          uniqueTaxonomyIds.add(taxonomyId);
          taxonomyRequests.push(
            authService.get(config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang))
              .then(response => {
                if (!response || !response.tid?.[0]?.value) {
                  console.error('Risposta tassonomia non valida:', response);
                  return null;
                }
                
                const taxonomyObject = {
                  title: response.name?.[0]?.value || 'Senza nome',
                  icon: response.field_icona?.[0]?.url || null,
                  pin: response.field_pin?.[0]?.url || null,
                  field_colore: response.field_colore || []
                };
                
                setTaxonomyData(prev => ({
                  ...prev,
                  [response.tid[0].value]: taxonomyObject
                }));
                
                loadedTaxonomyIds.current.add(taxonomyId);
              })
              .catch(error => {
                console.error(`Errore nel caricamento della tassonomia ${taxonomyId}:`, error);
                loadedTaxonomyIds.current.delete(taxonomyId);
              })
          );
        }
      });
    
      if (taxonomyRequests.length > 0) {
        await Promise.all(taxonomyRequests);
      }
      
      taxonomyLoadInProgress.current = false;
    } catch (error) {
      console.error('Errore durante il caricamento delle tassonomie:', error);
      taxonomyLoadInProgress.current = false;
    }
  }, [selectedLang]);
  
  const calculateFilteredPois = useCallback((selectedPOI, allPois, maxDistance) => {
    if (!selectedPOI?.field_geolocalizzazione?.[0] || !allPois?.length) return [];
    
    const { lat: selectedLat, lng: selectedLng } = selectedPOI.field_geolocalizzazione[0];
    if (!isFinite(selectedLat) || !isFinite(selectedLng)) return [];

    const poisWithDistance = allPois
      .map(poi => {
        if (!poi.field_geolocalizzazione?.[0]) return null;
        const { lat, lng } = poi.field_geolocalizzazione[0];
        if (!isFinite(lat) || !isFinite(lng)) return null;
        const distance = calculateDistance(selectedLat, selectedLng, lat, lng);
        return { ...poi, distance };
      })
      .filter(Boolean);

    const filteredPois = maxDistance === "all" 
      ? poisWithDistance 
      : poisWithDistance.filter(poi => poi.distance <= parseFloat(maxDistance));

    return filteredPois.sort((a, b) => {
      if (a.nid[0].value === selectedPOI.nid[0].value) return -1;
      if (b.nid[0].value === selectedPOI.nid[0].value) return 1;
      return a.distance - b.distance;
    });
  }, []);
  
  const createUserIcon = useCallback((isSelected = false) => {
    const zoom = mapRef.current ? mapRef.current.getZoom() : DEFAULT_ZOOM;
    const markerDimensions = calculateMarkerSize(zoom);
    
    const size = isSelected ? {
      width: Math.round(markerDimensions.size.width * 2),
      height: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.size;

    const anchor = isSelected ? {
      x: Math.round(markerDimensions.size.width * 2 / 2),
      y: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.anchor;

    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 719.4 842">
        <circle cx="362.7" cy="360.7" r="317.2" fill="#FF5555"/>
        <circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="#FF3333" stroke-width="60"/>
        <polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="#FF3333"/>
        <path d="M362.7 260 q-25 0-42.5-17.5 t-17.5-42.5 q0-25 17.5-42.5 t42.5-17.5 q25 0 42.5 17.5 t17.5 42.5 q0 25-17.5 42.5 t-42.5 17.5 Z" fill="#FFFFFF"/>
        <path d="M302.7 550 v-140 h-40 v-120 q0-25 17.5-42.5 t42.5-17.5 h80 q25 0 42.5 17.5 t17.5 42.5 v120 h-40 v140 Z" fill="#FFFFFF" transform="translate(0, 40)"/>
      </svg>`;

    return L.divIcon({
      html: svgString,
      iconSize: [size.width, size.height],
      iconAnchor: [anchor.x, anchor.y],
      className: 'map-marker user-location-marker'
    });
  }, []);
  
  const createPoiIcon = useCallback((poi, isSelected, number) => {
    if (!mapRef.current) return L.icon({
      iconUrl: null, // Fallback - dovrebbe mai essere usato
      iconSize: [50, 60],
      iconAnchor: [25, 60]
    });

    const zoom = mapRef.current.getZoom();
    const markerDimensions = calculateMarkerSize(zoom);
    const uniqueId = `poi-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
    
    // Logica di priorità per il colore: prima route, poi taxonomy, infine default
    let color = '#1E88E5'; // Colore predefinito
    
    if (route?.field_colore?.[0]?.color) {
      // Se il percorso ha un colore, usa quello
      color = route.field_colore[0].color;
    } else if (taxonomyId && taxonomyData[taxonomyId]?.field_colore?.[0]?.color) {
      // Altrimenti usa il colore della categoria
      color = taxonomyData[taxonomyId].field_colore[0].color;
    }
    
    let poiImage = getOptimizedPoiImage(poi, 'thumbnail', null); // Fallback gestito internamente

    const size = isSelected ? {
      width: Math.round(markerDimensions.size.width * 2),
      height: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.size;

    const anchor = isSelected ? {
      x: Math.round(markerDimensions.size.width * 2 / 2),
      y: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.anchor;

    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 719.4 842">
        <defs>
          <pattern id="imagePattern-${uniqueId}" patternUnits="userSpaceOnUse" width="634.4" height="634.4" x="45.5" y="43.5">
            <image x="30" y="-50" width="80%" height="80%" href="${poiImage}" preserveAspectRatio="xMidYMid slice"/>
          </pattern>
        </defs>
        <circle cx="362.7" cy="360.7" r="317.2" fill="url(#imagePattern-${uniqueId})"/>
        <circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="${color}" stroke-width="60"/>
        <polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="${color}"/>
        <text x="362.7" y="460" 
          text-anchor="middle" 
          fill="white" 
          font-size="280"
          font-weight="bold" 
          font-family="Titillium Web, Arial"
        >${number}</text>
      </svg>`;

    return L.divIcon({
      html: svgString,
      iconSize: [size.width, size.height],
      iconAnchor: [anchor.x, anchor.y],
      className: 'map-marker'
    });
  }, [taxonomyData, route]);
  
  const handleMarkerClick = useCallback((poi, index) => {
    if (!mapRef.current || !poi.field_geolocalizzazione?.[0]) return;
    if (selectedPOI && selectedPOI.nid[0].value === poi.nid[0].value) return;
    
    const map = mapRef.current;
    const { lat, lng } = poi.field_geolocalizzazione[0];
    
    if (!isFinite(lat) || !isFinite(lng)) return;

    const updateCardPosition = () => {
        if (sliderRef.current) {
            const cardContainer = sliderRef.current.querySelector('.card-container');
            if (cardContainer) {
                const cardWidth = cardContainer.getBoundingClientRect().width;
                sliderRef.current.scrollLeft = cardWidth * index;
            }
        }
    };

    if (!selectedPOI) {
      setSelectedPOI(poi);
      setActiveCardIndex(index);
      setVisiblePois(pois);
      setCardState('entering');
  
      setTimeout(() => {
          requestAnimationFrame(() => {
              if (sliderRef.current) {
                  const cardContainer = sliderRef.current.querySelector('.card-container');
                  if (cardContainer) {
                      
                      const cardWidth = cardContainer.getBoundingClientRect().width;
                      sliderRef.current.scrollLeft = cardWidth * index;
                      
                  }
              }
              setCardState('visible');
          });
      }, 0);
  
    } else {
        
        requestAnimationFrame(() => {
            setSelectedPOI(poi);
            
            setActiveCardIndex(index);
            
            setVisiblePois(pois);
            
            updateCardPosition();
            setCardState('visible');
            
        });
    }

    const pixelCenter = map.latLngToContainerPoint([lat, lng]);
    const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
    const newCenter = map.containerPointToLatLng(newPixelCenter);
    map.setView(newCenter, map.getZoom(), { animate: true });
}, [selectedPOI, pois]);

const handleScroll = useCallback(() => {
  if (!sliderRef.current) return;
  
  const slider = sliderRef.current;
  const scrollPosition = slider.scrollLeft;
  const cardElement = slider.querySelector('.card-container');
  if (!cardElement) return;
  
  const cardWidth = cardElement.getBoundingClientRect().width;
  const newIndex = Math.round(scrollPosition / cardWidth);
  
  setCanScrollLeft(scrollPosition > 0);
  setCanScrollRight(scrollPosition < slider.scrollWidth - slider.clientWidth - 1);

  if (newIndex >= 0 && newIndex < pois.length && newIndex !== activeCardIndex && cardState !== 'entering') {
    const newSelectedPoi = pois[newIndex];
    setActiveCardIndex(newIndex);
    
    if (mapRef.current && newSelectedPoi?.field_geolocalizzazione?.[0]) {
      const { lat, lng } = newSelectedPoi.field_geolocalizzazione[0];
      if (isFinite(lat) && isFinite(lng)) {
        const map = mapRef.current;
        setSelectedPOI(newSelectedPoi);
        
        const pixelCenter = map.latLngToContainerPoint([lat, lng]);
        const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
        const newCenter = map.containerPointToLatLng(newPixelCenter);
        
        map.setView(newCenter, map.getZoom(), { 
          animate: true,
          duration: 0.5
        });
      }
    }
  }
}, [activeCardIndex, pois, cardState]);

const handleClosePanel = useCallback(() => {
  setCardState('exiting');
  setTimeout(() => {
    setSelectedPOI(null);
    setVisiblePois([]);
    setCardState('hidden');
  }, ANIMATION_DELAY);
}, []);

const scrollToIndex = useCallback((index) => {
  if (!sliderRef.current || index < 0 || index >= pois.length) return;
  
  const slider = sliderRef.current;
  const cardElement = slider.querySelector('.card-container');
  if (!cardElement) return;
  
  const cardWidth = cardElement.getBoundingClientRect().width;
  const scrollPosition = cardWidth * index;
  
  slider.scrollTo({
    left: scrollPosition,
    behavior: 'smooth'
  });
}, [pois.length]);

const handleTouchStart = useCallback((e) => {
  if (!sliderRef.current) return;
  const slider = sliderRef.current;
  slider.dataset.touchStartX = e.touches[0].clientX;
  slider.dataset.scrollStartX = slider.scrollLeft;
}, []);

const handleTouchMove = useCallback((e) => {
  if (!sliderRef.current) return;
  e.preventDefault();
}, []);

const handleTouchEnd = useCallback((e) => {
  if (!sliderRef.current) return;
  const slider = sliderRef.current;
  const touchEndX = e.changedTouches[0].clientX;
  const touchStartX = parseFloat(slider.dataset.touchStartX);
  const diff = touchEndX - touchStartX;
  
  if (Math.abs(diff) > 50) {
    if (diff > 0 && activeCardIndex > 0) {
      scrollToIndex(activeCardIndex - 1);
    } else if (diff < 0 && activeCardIndex < pois.length - 1) {
      scrollToIndex(activeCardIndex + 1);
    }
  }
}, [activeCardIndex, pois.length, scrollToIndex]);

const MapController = React.memo(() => {
  const map = useMap();
  const location = useLocation();

  useEffect(() => {
    mapRef.current = map;
    
    // Reintroduciamo i gestori che aggiornano mapZoom
    const handleZoomAnim = (e) => setMapZoom(e.zoom);
    const handleZoomChange = () => setMapZoom(map.getZoom());
    
    map.on('zoomanim', handleZoomAnim);
    map.on('zoom', handleZoomChange);
    
    // Quando i mapBounds sono pronti, centro la mappa
    if (mapBounds && mapBounds.isValid() && !location.state?.selectedPOI && isInitialLoad) {
      map.fitBounds(mapBounds, { 
        padding: [PADDING, PADDING],
        maxZoom: 19
      });
      setIsInitialLoad(false);
    }

    if (location.state?.selectedPOI && selectedPOI && initialPoiDetailLoad) {
      const { lat, lng } = selectedPOI.field_geolocalizzazione[0];
      
      if (isFinite(lat) && isFinite(lng)) {
        map.setView([lat, lng], 17, { animate: false });
        
        setTimeout(() => {
          const pixelCenter = map.latLngToContainerPoint([lat, lng]);
          const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
          const newCenter = map.containerPointToLatLng(newPixelCenter);
          
          if (isFinite(newCenter.lat) && isFinite(newCenter.lng)) {
            map.setView(newCenter, 17, { 
              animate: true,
              duration: 0.5
            });
          }
        }, ANIMATION_DELAY);
      }
      
      setInitialPoiDetailLoad(false);
      setCardState('visible');
    }

    return () => {
      map.off('zoomanim', handleZoomAnim);
      map.off('zoom', handleZoomChange);
    };
  }, [map, mapBounds, location.state, selectedPOI, initialPoiDetailLoad, isInitialLoad]);

  return null;
});

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const routeResponse = await authService.get(config.API_ENDPOINTS.getNode(id, selectedLang));
      setRoute(routeResponse);

      if (routeResponse.field_poi) {
        routeCalculationDone.current = false;
        
        const poisPromises = routeResponse.field_poi.map(poi =>
          authService.get(config.API_ENDPOINTS.getNode(poi.target_id, selectedLang))
        );

        const poisResponses = await Promise.all(poisPromises);
        const validPois = poisResponses.filter(poi => {
          const coords = poi.field_geolocalizzazione?.[0];
          return coords && isFinite(coords.lat) && isFinite(coords.lng);
        });

        setPois(validPois);

        if (validPois.length > 0) {
          loadTaxonomies(validPois);
          
          const validCoordinates = validPois
            .map(poi => poi.field_geolocalizzazione[0])
            .filter(coords => coords && isFinite(coords.lat) && isFinite(coords.lng))
            .map(({ lat, lng }) => [lat, lng]);
            
          if (validCoordinates.length > 0) {
            const bounds = L.latLngBounds(validCoordinates);
            setMapBounds(bounds);
            
            if (validCoordinates.length > 1) {
              fetchOSRMRoute(validCoordinates);
            }
          }
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Errore durante il recupero dei dati:', error);
      setError(t('erroreCaricamentoDati'));
      setLoading(false);
    }
  };

  const watchId = fetchUserLocation();
  fetchData();

  return () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
    }
  };
}, [id, fetchUserLocation, loadTaxonomies]);

useEffect(() => {
  const slider = sliderRef.current;
  if (!slider) return;

  handleScroll();
  slider.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleScroll);

  return () => {
    slider.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleScroll);
  };
}, [handleScroll]);

const openInfoPanel = useCallback(() => {
  if (!route) {
    console.error('Impossibile aprire il pannello: nessun percorso caricato');
    return;
  }
  
  setIsInfoPanelOpen(true);
}, [route]);

if (error) return <div className="alert alert-danger m-3">{error}</div>;

return (
  <div style={{ height: 'calc(100dvh - 140px)', scrollSnapAlign: 'start', position: 'relative' }}>
    <MapContainer 
      center={ITALY_CENTER} 
      zoom={DEFAULT_ZOOM} 
      id="map" 
      zoomControl={false}
    >
      <MapController />
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        minZoom={5}
        maxZoom={19}
      />
      <BackButton className="goback-map"/>
      
      <div className="position-absolute d-flex gap-2" style={{ top: '6px', right: '8px', zIndex: 1000 }}>
        <button
          onClick={openInfoPanel}
          className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
          style={{ width: '40px', height: '40px' }}
          aria-label="informazioniPercorso"
        >
          <Icon name="info" />
        </button>

        <button
          aria-label="{t('miaPosizione')}"
          className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
          style={{ width: '40px', height: '40px' }}
          onClick={() => {
            if (userLocation && mapRef.current) {
              const map = mapRef.current;
              const { latitude, longitude } = userLocation;
              
              setIsUserMarkerSelected(true);
              setTimeout(() => setIsUserMarkerSelected(false), 2000);
              
              const pixelCenter = map.latLngToContainerPoint([latitude, longitude]);
              const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
              const newCenter = map.containerPointToLatLng(newPixelCenter);
              map.setView(newCenter, map.getZoom(), { animate: true });
              
              handleClosePanel();
              setSelectedPOI(null);
            }
          }}
        >
          <Icon name="my_location" />
        </button>
      </div>

      {routePath.length > 0 && (
        <Polyline 
          positions={routePath}
          color={route?.field_colore?.[0]?.color || "#1E88E5"}
          weight={4}
          opacity={0.6}
        />
      )}
      
      {pois.map((poi, index) => {
        const latLng = poi.field_geolocalizzazione?.[0];
        if (!latLng || !isFinite(latLng.lat) || !isFinite(latLng.lng)) return null;
        
        const { lat, lng } = latLng;
        const isSelected = selectedPOI && selectedPOI.nid[0].value === poi.nid[0].value;

        return (
          <Marker
            key={poi.nid[0].value}
            position={[lat, lng]}
            icon={createPoiIcon(poi, isSelected, index + 1)}
            eventHandlers={{
              click: () => handleMarkerClick(poi, index),
            }}
            zIndexOffset={isSelected ? 1000 : 0}
          />
        );
      })}

      {userLocation && isFinite(userLocation.latitude) && isFinite(userLocation.longitude) && (
        <Marker
          position={[userLocation.latitude, userLocation.longitude]}
          icon={createUserIcon(isUserMarkerSelected)}
          interactive={false}
        />
      )}
    </MapContainer>

    {isInfoPanelOpen && (
    <RouteInfoPanel 
      route={route}
      pois={pois}
      segmentDistances={segmentDistances}
      onClose={() => setIsInfoPanelOpen(false)}
    />
  )}

  {selectedPOI && (
    <div className={`map-card ${cardState}`}>
      <div className="position-relative">
        {canScrollLeft && (
          <button
            className="btn bg-white border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute start-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
            onClick={() => scrollToIndex(activeCardIndex - 1)}
            aria-label=" {t('precedente')}"
          >
            <Icon name="chevron_left" style={{ fontSize: '16px' }} />
          </button>
        )}
        
        {canScrollRight && (
          <button
            className="btn bg-white border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute end-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
            onClick={() => scrollToIndex(activeCardIndex + 1)}
            aria-label="{t('successivo')}"
          >
            <Icon name="chevron_right" style={{ fontSize: '16px' }} />
          </button>
        )}

<div 
          ref={sliderRef}
          className="row flex-nowrap carousel-container"
          style={{
            scrollbarWidth: 'none',
            position: 'relative',
            padding: '0 8px',
            scrollSnapType: 'x mandatory',
            overflowX: 'hidden',
            touchAction: 'none'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
  {visiblePois.map(poi => (
    <div 
      key={poi.nid[0].value} 
      className="card-container card-containerlite px-1"
    >
      <PoiCardLite
        poi={poi}
        taxonomyData={taxonomyData}
        distance={
          userLocation && poi.field_geolocalizzazione?.[0] ?
          `${calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            poi.field_geolocalizzazione[0].lat,
            poi.field_geolocalizzazione[0].lng
          ).toFixed(1)} km` :
          '...'
        }
        onClose={handleClosePanel}
        handleNavigate={() => navigate(`/poi/${poi.nid[0].value}`)}
      />
    </div>
  ))}
</div>
      </div>
    </div>
  )}
</div>
);
};

export default RouteMap;